import { useEffect, useState } from "react";
import {
  BarChart3, BriefcaseBusiness, ClipboardList, FileText, HelpCircle, Inbox, LayoutDashboard,
  LogOut, Menu, Plus, Search, Settings2, ShieldCheck, UserCheck, Users, UsersRound, X,
  Sparkles,
} from "lucide-react";
import { http, roleLabels, notify, can } from "../api";
import GlobalSearch from "./GlobalSearch";
import Dashboard from "./Dashboard";
import Leads from "./Leads";
import LeadDetail from "./LeadDetail";
import Applications from "./Applications";
import Reports from "./Reports";
import Documents from "./Documents";
import Audit from "./Audit";
import AdminPanel from "./AdminPanel";
import TeamRoster from "./TeamRoster";
import TeamOps from "./TeamOps";
import ReminderBell from "./ReminderBell";
import Help from "./Help";
import { LeadForm } from "./forms";
import { CallingDataTab, MyFollowupsTab, ManagerTeamDashboard, PerformanceReports } from "./CallingWorkspace";
import PolicyFinder from "./PolicyFinder";
import HRPanel from "./HRPanel";
import CallingPerformanceReport from "./CallingPerformanceReport";
import WebLeads from "./WebLeads";
import { useFollowupReminders } from "../hooks/useFollowupReminders";

const ROSTER_ROLES = ["admin", "business_head", "sales_manager", "back_office_manager"];

const navConfig = [
  { id: "dashboard", label: "Overview", icon: LayoutDashboard, permission: null },
  { id: "calling_data", label: "Calling Data", icon: Users, permission: null,
    show: (u) => u.role === "telecaller" },
  { id: "my_followups", label: "My Follow-ups", icon: ClipboardList, permission: null,
    show: (u) => u.role === "telecaller" },
  { id: "web_leads", label: "Web Leads", icon: Inbox, permission: "view_leads",
    show: (u) => ["admin", "sub_admin", "business_head", "sales_manager"].includes(u.role) },
  { id: "leads", label: "Login Status", icon: Users, permission: "view_leads",
    show: (u) => u.role !== "sub_admin" },
  { id: "policy_finder", label: "Policy Finder", icon: Search, permission: null,
    show: (u) => ["admin", "business_head", "sales_manager", "telecaller", "back_office_manager", "back_office_executive"].includes(u.role) },
  { id: "applications", label: "Applications", icon: BriefcaseBusiness, permission: "view_leads",
    show: (u) => u.role !== "sub_admin" },
  { id: "team_dashboard", label: "Team dashboard", icon: BarChart3, permission: null,
    show: (u) => u.role === "sales_manager" || u.role === "admin" || u.role === "business_head" },
  { id: "team_ops", label: "Sales team", icon: UserCheck,
    permission: null,
    show: (u) => u.role === "admin" ||
                 ((u.role !== "sub_admin") && (
                   can(u, "approve_sales_associates") || can(u, "view_sa_performance") ||
                   can(u, "view_calling_activity")
                 )) },
  { id: "hr", label: "HR", icon: Sparkles, permission: null,
    show: (u) => u.role === "admin" || u.role === "sub_admin" },
  { id: "roster", label: "Team roster", icon: UsersRound, permission: null, rolesOnly: ROSTER_ROLES },
  { id: "performance_reports", label: "Performance", icon: BarChart3, permission: null,
    show: (u) => u.role === "telecaller" },
  { id: "calling_performance", label: "Data Calling Performance", icon: BarChart3, permission: null,
    show: (u) => ["admin", "business_head", "sales_manager"].includes(u.role) },
  { id: "reports", label: "Reports", icon: BarChart3, permission: "view_reports",
    show: (u) => u.role !== "sub_admin" },
  { id: "documents", label: "Documents", icon: FileText, permission: "view_documents",
    show: (u) => u.role !== "sub_admin" },
  { id: "audit", label: "Audit log", icon: ShieldCheck, permission: null },
];

export default function Shell({ user, onLogout }) {
  const [view, setView] = useState("dashboard");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadFilters, setLeadFilters] = useState({});

  // App-wide follow-up reminder polling for SAs — pops a toast + browser
  // Notification the moment a follow-up hits its scheduled time.
  useFollowupReminders(user);

  // Web-leads badge — polls untriaged raw inquiry count every 60s.
  const [webLeadCount, setWebLeadCount] = useState(0);
  const canSeeWebLeads = ["admin", "sub_admin", "business_head", "sales_manager"].includes(user.role);
  useEffect(() => {
    if (!canSeeWebLeads) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await http.get("/web-leads/count");
        if (alive) setWebLeadCount(r.data?.count || 0);
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [canSeeWebLeads, refresh]);

  const handleLogout = async () => {
    try {
      await http.post("/auth/logout");
    } catch {
      // ignore — server may already have expired the session
    }
    notify.info("You've been signed out.");
    onLogout();
  };

  const nav = navConfig.filter((n) => {
    if (n.rolesOnly && !n.rolesOnly.includes(user.role)) return false;
    if (n.show && !n.show(user)) return false;
    if (n.permission && !can(user, n.permission)) return false;
    return true;
  });

  const go = (v) => {
    setView(v);
    setSelected(null);
    setOpen(false);
  };

  const title = selected
    ? "Lead detail"
    : view === "settings"
    ? "Admin configuration"
    : view === "help"
    ? "User guide"
    : nav.find((x) => x.id === view)?.label || "Overview";

  const openLead = (leadId) => {
    if (view !== "leads") setView("leads");
    setSelected(leadId);
  };

  return (
    <div className="shell">
      {open && (
        <button
          className="rail-scrim mobile-only"
          data-testid="mobile-menu-scrim"
          onClick={() => setOpen(false)}
          aria-label="Close menu"
        />
      )}
      <aside className={open ? "rail open" : "rail"}>
        <div className="rail-brand">
          <span className="brand-mark small">RX</span>
          <span>
            RUPEE<span className="accent">XPERT</span>
          </span>
          <button
            className="icon-button mobile-only rail-brand-logout"
            data-testid="logout-button-mobile"
            onClick={handleLogout}
            aria-label="Log out"
            title="Log out"
          >
            <LogOut size={18} />
          </button>
          <button
            className="icon-button mobile-only"
            data-testid="mobile-menu-close-button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>
        <div className="scope">
          <span className="scope-dot" />
          <div>
            <strong data-testid="sidebar-scope-label">{roleLabels[user.role]}</strong>
            <small>{user.team_name}</small>
          </div>
        </div>
        <nav>
          {nav.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`nav-${id}-button`}
              className={view === id && !selected ? "nav-item active" : "nav-item"}
              onClick={() => go(id)}
            >
              <Icon size={18} />
              <span>{label}</span>
              {id === "web_leads" && webLeadCount > 0 && (
                <span className="nav-badge" data-testid="nav-web-leads-badge">{webLeadCount}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="rail-footer">
          <button
            className={view === "help" ? "nav-item active" : "nav-item"}
            data-testid="nav-help-button"
            onClick={() => go("help")}
          >
            <HelpCircle size={18} />
            <span>Help</span>
          </button>
          {user.role === "admin" && (
            <button
              className={view === "settings" ? "nav-item active" : "nav-item"}
              data-testid="nav-settings-button"
              onClick={() => go("settings")}
            >
              <Settings2 size={18} />
              <span>Settings</span>
            </button>
          )}
          <div className="user-mini">
            {user.photo_url ? (
              <img className="avatar"
                   data-testid="sidebar-avatar-image"
                   src={user.photo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${user.photo_url}` : user.photo_url}
                   alt={user.name}
                   style={{ objectFit: "cover" }} />
            ) : (
              <div className="avatar">{user.name?.slice(0, 2).toUpperCase()}</div>
            )}
            <div>
              <strong>{user.name}</strong>
              <small>{roleLabels[user.role]}</small>
            </div>
            <button
              data-testid="logout-button"
              className="logout"
              onClick={handleLogout}
              aria-label="Log out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <button
            className="icon-button mobile-only"
            data-testid="mobile-menu-open-button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <div>
            <p className="eyebrow">WORKSPACE / {user.team_name?.toUpperCase()}</p>
            <h2 data-testid="page-title">{title}</h2>
          </div>
          <div className="top-actions">
            <GlobalSearch onSelectLead={openLead} />
            <ReminderBell user={user} onOpenLead={openLead} />
            {can(user, "create_leads") && (
              <button
                data-testid="header-create-lead-button"
                className="primary-button compact"
                onClick={() => setShowLeadForm(true)}
              >
                <Plus size={16} /> New lead
              </button>
            )}
            {user.photo_url ? (
              <img className="avatar large"
                   data-testid="topbar-avatar-image"
                   src={user.photo_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${user.photo_url}` : user.photo_url}
                   alt={user.name}
                   style={{ objectFit: "cover" }} />
            ) : (
              <div className="avatar large">{user.name?.slice(0, 2).toUpperCase()}</div>
            )}
          </div>
        </header>

        <div className="page-content">
          {selected ? (
            <LeadDetail leadId={selected} user={user} onBack={() => setSelected(null)} />
          ) : (
            <>
              {view === "dashboard" && (user.role === "sub_admin"
                ? <HRPanel user={user} />
                : <Dashboard user={user} onNavigate={go} />)}
              {view === "calling_data" && <CallingDataTab user={user} onNavigate={go} onOpenLead={openLead} />}
              {view === "my_followups" && <MyFollowupsTab onOpenLead={openLead} />}
              {view === "team_dashboard" && <ManagerTeamDashboard />}
              {view === "performance_reports" && <PerformanceReports />}
              {view === "calling_performance" && <CallingPerformanceReport user={user} />}
              {view === "leads" && (
                <Leads onSelect={setSelected} refresh={refresh} user={user}
                       initialFilters={leadFilters}
                       onClearFilters={() => setLeadFilters({})} />
              )}
              {view === "web_leads" && (
                <WebLeads
                  user={user}
                  onTriaged={() => setRefresh((x) => x + 1)}
                />
              )}
              {view === "applications" && <Applications user={user} onSelect={openLead} />}
              {view === "policy_finder" && <PolicyFinder />}
              {view === "roster" && (
                <TeamRoster
                  onOpenMember={(memberId, memberName) => {
                    setLeadFilters({ assigned_to: memberId, assigned_name: memberName });
                    setView("leads");
                    setSelected(null);
                  }}
                />
              )}
              {view === "team_ops" && <TeamOps user={user} />}
              {view === "hr" && <HRPanel user={user} />}
              {view === "reports" && <Reports user={user} />}
              {view === "documents" && <Documents user={user} onSelect={openLead} />}
              {view === "audit" && <Audit />}
              {view === "settings" && <AdminPanel user={user} />}
              {view === "help" && <Help user={user} />}
            </>
          )}
        </div>
      </section>

      {showLeadForm && (
        <LeadForm
          user={user}
          onClose={() => setShowLeadForm(false)}
          onSaved={(savedLead) => {
            setShowLeadForm(false);
            setRefresh((x) => x + 1);
            // Auto-open Review & Edit page for the freshly-created lead.
            // The lead is in `pending_review` — SA can edit any field
            // (mobile stays read-only) then click "Send to Login".
            if (savedLead?.lead_id) {
              openLead(savedLead.lead_id);
            } else {
              go("leads");
            }
          }}
        />
      )}
    </div>
  );
}

// Icon re-exported for use elsewhere (kept for readability of imports)
export { ClipboardList };
